import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { submitLead } from '../services/api.js';

// Public "Book a call" funnel for Alfred Voice v02 SaaS. Replaces direct
// Stripe checkout for new prospects — capture qualifying info, redirect
// to a Cal.com booking link, follow up by hand. Stripe stays on for
// existing customers signing up for an additional plan.

const PLANS = [
  {
    id: 'core',
    name: 'Core',
    price: '$149/mo + $299 setup',
    blurb: '100 calls/mo. AI on the first ring. Forwarding, message + email summaries. Best for small teams.',
    feature: ['100 inbound calls / month', 'AI answers on the first ring', 'Forward to owner', 'Message + email summaries']
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '$449/mo + $599 setup',
    blurb: 'Most popular. 600 calls/mo, call routing, structured data capture, searchable history.',
    feature: ['600 inbound calls / month', 'Call routing across teams', 'Structured-data capture', 'Searchable transcripts'],
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$899/mo + custom setup',
    blurb: '1,500 calls/mo, multi-location routing, advanced KB integration, full transcription archive.',
    feature: ['1,500 inbound calls / month', 'Multi-location routing', 'Advanced KB integration', 'Full transcription archive']
  }
];

const VOLUMES = [
  { v: '<100', label: 'Under 100 / month' },
  { v: '100-500', label: '100–500 / month' },
  { v: '500-1500', label: '500–1,500 / month' },
  { v: '1500+', label: '1,500+ / month' }
];

const TIMELINES = [
  { v: 'immediate', label: "Now — I'm ready" },
  { v: '30d', label: 'Within 30 days' },
  { v: '90d', label: 'Within 90 days' },
  { v: 'exploring', label: 'Just exploring' }
];

export default function Book() {
  const [search] = useSearchParams();
  const [step, setStep] = useState(1);
  const [plan, setPlan] = useState(search.get('plan') || '');
  const [form, setForm] = useState({
    email: '', name: '', companyName: '', phone: '',
    useCase: '', expectedVolume: '', timeline: '', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // { bookingUrl }

  const pickPlan = (id) => { setPlan(id); setStep(2); };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.email) { setError('Email is required.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const r = await submitLead({
        ...form,
        plan: plan || undefined,
        source: 'book-page'
      });
      setDone({ bookingUrl: r?.data?.bookingUrl || null });
    } catch (err) {
      setError(err.message || 'Submit failed. Please try again or email hello@relaywork.co.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <header className="border-b border-stone-900">
        <div className="mx-auto max-w-5xl px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-alfred-gold font-serif text-2xl">Alfred</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-stone-500">Voice</span>
          </Link>
          <Link to="/login" className="text-sm text-stone-300 hover:text-alfred-gold">Customer sign-in</Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-14">
        {done ? (
          <SuccessPanel bookingUrl={done.bookingUrl} email={form.email} />
        ) : step === 1 ? (
          <PlansStep plans={PLANS} onPick={pickPlan} />
        ) : (
          <FormStep
            plan={PLANS.find((p) => p.id === plan)}
            form={form}
            setForm={setForm}
            error={error}
            submitting={submitting}
            onSubmit={submit}
            onBack={() => setStep(1)}
          />
        )}
      </main>

      <footer className="border-t border-stone-900 mt-16">
        <div className="mx-auto max-w-5xl px-6 py-8 text-xs text-stone-500 flex items-center justify-between">
          <span>© Relay Work · Alfred Voice</span>
          <span className="space-x-4">
            <Link to="/faq" className="hover:text-alfred-gold">FAQ</Link>
            <Link to="/privacy" className="hover:text-alfred-gold">Privacy</Link>
            <Link to="/terms" className="hover:text-alfred-gold">Terms</Link>
          </span>
        </div>
      </footer>
    </div>
  );
}

function PlansStep({ plans, onPick }) {
  return (
    <>
      <h1 className="text-4xl font-serif">Pick the plan that fits — book a 15-minute call.</h1>
      <p className="mt-3 text-stone-400 max-w-2xl">
        Alfred Voice is a managed AI phone agent. We configure it together on a
        short discovery call, no checkout pressure. Pick a plan to start the
        conversation; pricing locks in only after we both decide it's a fit.
      </p>

      <div className="mt-10 grid gap-5 md:grid-cols-3">
        {plans.map((p) => (
          <button
            key={p.id}
            onClick={() => onPick(p.id)}
            className={`text-left rounded-2xl border p-6 transition hover:border-alfred-gold/60 hover:shadow-lg hover:shadow-alfred-gold/10 ${p.popular ? 'border-alfred-gold/50 ring-1 ring-alfred-gold/30' : 'border-stone-800'}`}
          >
            {p.popular && (
              <span className="badge bg-alfred-gold/15 text-alfred-gold border border-alfred-gold/30 mb-3 inline-block">Most popular</span>
            )}
            <h3 className="text-2xl font-serif text-stone-100">{p.name}</h3>
            <p className="mt-1 text-alfred-gold text-sm">{p.price}</p>
            <p className="mt-3 text-sm text-stone-300">{p.blurb}</p>
            <ul className="mt-5 space-y-1.5 text-xs text-stone-400">
              {p.feature.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <span className="text-alfred-gold">•</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-alfred-gold">
              <span>Book a call</span> <span aria-hidden="true">→</span>
            </div>
          </button>
        ))}
      </div>

      <p className="mt-8 text-sm text-stone-500">
        Already a customer? <Link to="/login" className="text-alfred-gold hover:underline">Sign in →</Link>
      </p>
    </>
  );
}

function FormStep({ plan, form, setForm, error, submitting, onSubmit, onBack }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-stone-400 hover:text-alfred-gold mb-6"
      >
        ← Back to plans
      </button>

      <h1 className="text-3xl font-serif">Tell me a little about your business.</h1>
      {plan && (
        <p className="mt-2 text-stone-400">
          Plan in mind: <span className="text-alfred-gold">{plan.name} — {plan.price}</span>. We'll
          confirm the right tier on the call.
        </p>
      )}

      {error && (
        <p className="mt-6 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</p>
      )}

      <form onSubmit={onSubmit} className="mt-8 space-y-5 max-w-xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="field-label">Your name</label>
            <input className="field-input" value={form.name} onChange={(e) => set('name', e.target.value)} />
          </div>
          <div>
            <label className="field-label">Email *</label>
            <input className="field-input" type="email" required value={form.email} onChange={(e) => set('email', e.target.value)} />
          </div>
          <div>
            <label className="field-label">Company</label>
            <input className="field-input" value={form.companyName} onChange={(e) => set('companyName', e.target.value)} />
          </div>
          <div>
            <label className="field-label">Phone</label>
            <input className="field-input" type="tel" placeholder="+1…" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          </div>
        </div>

        <div>
          <label className="field-label">What do you want Alfred to handle?</label>
          <textarea
            className="field-input min-h-[80px]"
            placeholder="e.g. Inbound calls to a 6-clinic practice; lots of after-hours calls we miss; want to book appointments and route urgent cases."
            value={form.useCase}
            onChange={(e) => set('useCase', e.target.value)}
          />
        </div>

        <div>
          <label className="field-label">Expected call volume</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {VOLUMES.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => set('expectedVolume', o.v)}
                className={`rounded-lg border px-3 py-2 text-sm text-left transition ${form.expectedVolume === o.v ? 'border-alfred-gold bg-alfred-gold/10 text-alfred-gold' : 'border-stone-800 text-stone-300 hover:border-stone-700'}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="field-label">Timeline</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {TIMELINES.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => set('timeline', o.v)}
                className={`rounded-lg border px-3 py-2 text-sm text-left transition ${form.timeline === o.v ? 'border-alfred-gold bg-alfred-gold/10 text-alfred-gold' : 'border-stone-800 text-stone-300 hover:border-stone-700'}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="field-label">Anything else?</label>
          <textarea
            className="field-input min-h-[80px]"
            placeholder="Optional — anything that helps the call be useful."
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
        </div>

        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'Submitting…' : 'Continue → schedule the call'}
        </button>
        <p className="text-xs text-stone-500">
          Submitting takes you to a calendar to pick a time. We'll confirm by email.
        </p>
      </form>
    </>
  );
}

function SuccessPanel({ bookingUrl, email }) {
  // Auto-redirect to the booking URL after a short delay so the user
  // sees the confirmation screen first.
  if (bookingUrl) {
    setTimeout(() => { window.location.href = bookingUrl; }, 2500);
  }
  return (
    <div className="text-center max-w-xl mx-auto py-10">
      <CheckCircle2 size={56} className="text-alfred-gold mx-auto" />
      <h2 className="mt-6 text-3xl font-serif">Got it — talk soon.</h2>
      <p className="mt-3 text-stone-400">
        We've recorded your details ({email}) and will email you a confirmation.
      </p>
      {bookingUrl ? (
        <p className="mt-6 text-stone-300">
          Redirecting to scheduling…{' '}
          <a href={bookingUrl} className="text-alfred-gold hover:underline">click here if it doesn't load.</a>
        </p>
      ) : (
        <p className="mt-6 text-stone-300">
          We'll reach out within one business day to schedule a call.
        </p>
      )}
      <Link to="/" className="mt-10 inline-block text-sm text-stone-400 hover:text-alfred-gold">
        ← Back home
      </Link>
    </div>
  );
}
